import {endpoints, headerConfigs} from "@/utils/config";

const parseResponse = async (result, fallbackMessage) => {
  const rawText = await result.text();
  let body = null;
  try {
    body = rawText ? JSON.parse(rawText) : null;
  } catch {
    body = null;
  }

  if (!result.ok) {
    let message =
      body?.description || body?.message || fallbackMessage;
    if (result.status === 429) {
      message = "Too many requests. Please wait a moment and try again.";
    }
    const err = new Error(message);
    err.status = result.status;
    err.body = body ?? rawText;
    throw err;
  }
  return body;
};

export const fetchCart = async () => {
  const result = await fetch(endpoints.cart, {
    method: "GET",
    ...headerConfigs,
  });
  return parseResponse(result, "Unable to load cart.");
};

export const fetchAddItem = async payload => {
  let products = Array.isArray(payload) ? payload : [payload];

  try {
    const result = await fetch(endpoints.add, {
      method: "POST",
      body: JSON.stringify({
        items: products,
      }),
      ...headerConfigs,
    });
    return await parseResponse(result, "Unable to add to cart.");
  } catch (error) {
    console.error(error.message);
    throw error;
  }
};

export const applyDiscountLineItem = async ({discountCode}) => {
  const url = new URL(`${location.origin}/discount/${discountCode}`);

  return await fetch(url, {
    method: "GET",
    ...headerConfigs,
  });
};

export const clearDiscountCode = async () => {
  try {
    const result = await fetch(endpoints.update, {
      method: "POST",
      body: JSON.stringify({
        discount: "",
      }),
      ...headerConfigs,
    });
    return await parseResponse(result, "Unable to update cart.");
  } catch (error) {
    console.error(error.message);
    throw error;
  }
};

/**
 * Takes the key and the quantity already decided. The store applies the change
 * on screen first and only then queues the request, so by the time this runs
 * `item.quantity` has moved on — deriving the quantity here would send the
 * wrong one. The key is resolved by the caller for the same reason: a line
 * added a moment ago has a different key now than when it was clicked.
 */
export const fetchUpdateItem = async ({key, quantity}) => {
  try {
    const result = await fetch(endpoints.update, {
      method: "POST",
      body: JSON.stringify({
        updates: {[key]: quantity},
      }),
      ...headerConfigs,
    });
    return await parseResponse(result, "Unable to update cart.");
  } catch (error) {
    console.error(error.message);
    throw error;
  }
};

/** Takes the keys, resolved by the caller when the request runs. */
export const fetchRemoveItem = async keys => {
  const mappedPayload = (Array.isArray(keys) ? keys : [keys]).reduce(
    (acc, key) => {
      acc[key] = 0;
      return acc;
    },
    {},
  );

  try {
    const result = await fetch(endpoints.update, {
      method: "POST",
      body: JSON.stringify({
        updates: mappedPayload,
      }),
      ...headerConfigs,
    });
    return await parseResponse(result, "Unable to update cart.");
  } catch (error) {
    console.error(error.message);
    throw error;
  }
};

/**
 * Switches the plan on the line itself. `change.js` does it in one request and
 * the line keeps its properties and its place in the cart; removing and adding
 * it back took two and moved it to the end.
 */
export const fetchSwapSellingPlan = async ({key, quantity, planId}) => {
  try {
    const result = await fetch(endpoints.change, {
      method: "POST",
      body: JSON.stringify({
        id: key,
        quantity,
        selling_plan: planId || null,
      }),
      ...headerConfigs,
    });
    return await parseResponse(result, "Unable to update subscription.");
  } catch (error) {
    console.error(error.message);
    throw error;
  }
};

export const fetchProductByHandle = async handle => {
  try {
    const result = await fetch(`/products/${handle}.js`, {
      method: "GET",
      ...headerConfigs,
    });
    if (!result.ok) return null;
    return await result.json();
  } catch (error) {
    console.error(error.message);
    return null;
  }
};
